/**
 * 設定頁「偏好」段（設計 v2 S7 前半；UI v2 **步 9**）
 *
 *   字體大小（5 段；`@coeldery/font-scale`）／外觀（日間／夜晚／跟系統）／語言／通知
 *   ⚠️ 通知掣屬**設計 R-14**：Web Push 後端未接通 → 現階段只係**記住偏好**（未會真通知）
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLocale, SUPPORTED_LOCALES, type SupportedLocale } from '../../i18n';
import { useAppearance } from './useAppearance';

const LOCALE_LABEL_KEYS: Record<SupportedLocale, string> = {
  'zh-HK': 'locale_zh_hk',
  en: 'locale_en',
  'zh-CN': 'locale_zh_cn',
};

export default function PreferenceSections() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme, fontLevel, fontLevels, setFontLevel } = useAppearance();
  const current = i18n.language as SupportedLocale;

  const [notify, setNotify] = useState(() => {
    try {
      return localStorage.getItem('cosingery.notify') === 'on';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('cosingery.notify', notify ? 'on' : 'off');
    } catch {
      /* 私隱模式 */
    }
  }, [notify]);

  return (
    <>
      {/* 字體大小（設計 C16） */}
      <section className="settings__group" data-testid="settings-font-size">
        <h2 className="section-title">{t('settings_font_size')}</h2>
        <p className="footnote">{t('settings_font_size_hint')}</p>
        <div className="scale" role="group">
          {fontLevels.map((l) => (
            <button
              key={l.key}
              type="button"
              data-testid={`font-scale-${l.key}`}
              aria-pressed={fontLevel === l.key}
              className={'scale__opt' + (fontLevel === l.key ? ' scale__opt--on' : '')}
              onClick={() => setFontLevel(l.key)}
            >
              <b>Aa</b>
              <span>{t(`font_scale_${l.key}`)}</span>
            </button>
          ))}
        </div>
        <p className="scale__preview hand">{t('font_scale_preview')}</p>
      </section>

      {/* 外觀 */}
      <section className="settings__group" data-testid="settings-theme">
        <h2 className="section-title">{t('settings_theme')}</h2>
        <div className="locales" role="group">
          {(
            [
              ['light', 'theme_light'],
              ['dark', 'theme_dark'],
              ['system', 'theme_system'],
            ] as const
          ).map(([id, key]) => (
            <button
              key={id}
              type="button"
              className={'lang-btn' + (theme === id ? ' lang-btn--active' : '')}
              aria-pressed={theme === id}
              onClick={() => setTheme(id)}
            >
              {t(key)}
            </button>
          ))}
        </div>
      </section>

      {/* 語言（設計 C15） */}
      <section className="settings__group" data-testid="settings-language">
        <h2 className="section-title">{t('language_label')}</h2>
        <div className="locales" role="group">
          {SUPPORTED_LOCALES.map((code) => (
            <button
              key={code}
              type="button"
              lang={code}
              className={'lang-btn' + (current === code ? ' lang-btn--active' : '')}
              aria-pressed={current === code}
              onClick={() => changeLocale(code)}
            >
              {t(LOCALE_LABEL_KEYS[code])}
            </button>
          ))}
        </div>
      </section>

      {/* 通知（⚠️ R-14：push 未接通 → 現階段只記住偏好） */}
      <section className="settings__group" data-testid="settings-notify">
        <h2 className="section-title">{t('settings_notify')}</h2>
        <div className="settings__row">
          <span>{t('notify_song_ready')}</span>
          <button
            type="button"
            role="switch"
            data-testid="notify-song-ready"
            aria-checked={notify}
            aria-label={t('notify_song_ready')}
            className="switch"
            onClick={() => setNotify((v) => !v)}
          />
        </div>
        <p className="footnote">{t('notify_song_ready_hint')}</p>
      </section>
    </>
  );
}
